import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PrivacyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #FF9500 0%, #FFB84D 100%);
  color: white;
  text-align: center;
  padding: 60px 20px;
  margin-bottom: 40px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(255, 149, 0, 0.2);
  animation: ${fadeInUp} 0.8s ease;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  background: rgba(255,255,255,0.2);
  color: white;
  border: 2px solid rgba(255,255,255,0.3);
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-2px);
  }
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease 0.2s both;
`;

const ContentBody = styled.div`
  padding: 40px;
  line-height: 1.7;
  color: #333;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const SectionTitle = styled.h2`
  color: #FF9500;
  font-size: 1.8rem;
  font-weight: 700;
  margin: 40px 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 3px solid #FFE5B4;
  position: relative;
  
  &:first-child {
    margin-top: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 60px;
    height: 3px;
    background: #FF9500;
  }
`;

const SubSectionTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 25px 0 15px 0;
  color: #2c3e50;
`;

const InfoBox = styled.div`
  padding: 20px;
  border-radius: 12px;
  margin: 20px 0;
  border-left: 5px solid ${props => props.type === 'warning' ? '#f39c12' : props.type === 'success' ? '#27ae60' : props.type === 'info' ? '#3498db' : '#e74c3c'};
  background: ${props => props.type === 'warning' ? '#fff3cd' : props.type === 'success' ? '#d4edda' : props.type === 'info' ? '#cce5ff' : '#f8d7da'};
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
`;

const InfoTitle = styled.h4`
  margin: 0 0 10px 0;
  color: ${props => props.type === 'warning' ? '#856404' : props.type === 'success' ? '#155724' : props.type === 'info' ? '#004085' : '#721c24'};
  font-size: 1.1rem;
  font-weight: 600;
`;

const ContactBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 16px;
  margin: 30px 0;
  text-align: center;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
`;

const ContactTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 15px;
  color: white;
`;

const ContactButton = styled.a`
  display: inline-block;
  background: rgba(255,255,255,0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  margin: 5px 10px;
  transition: all 0.3s ease;
  border: 2px solid rgba(255,255,255,0.3);
  
  &:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-2px);
    color: white;
    text-decoration: none;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin: 30px 0;
`;

const SummaryCard = styled.div`
  background: ${props => props.type === 'do' ? '#e8f5e8' : props.type === 'dont' ? '#ffe5e5' : '#e5f3ff'};
  padding: 20px;
  border-radius: 12px;
  border: 1px solid ${props => props.type === 'do' ? '#a8d5a8' : props.type === 'dont' ? '#ffb3b3' : '#b3d9ff'};
`;

const SummaryTitle = styled.h4`
  color: ${props => props.type === 'do' ? '#27ae60' : props.type === 'dont' ? '#e74c3c' : '#3498db'};
  margin: 0 0 10px 0;
  font-size: 1.1rem;
`;

const StyledList = styled.ul`
  padding-left: 20px;
  margin: 15px 0;
  
  li {
    margin-bottom: 8px;
    line-height: 1.6;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(120deg, #FFE5B4 0%, #FFD700 100%);
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  color: #8B4513;
`;

const UpdateDate = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #FF9500;
  margin-bottom: 20px;
  font-weight: 600;
  color: #495057;
`;

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <PrivacyContainer>
      <Header>
        <HeaderTitle>🛡️ Privacy Policy</HeaderTitle>
        <HeaderSubtitle>Trasparenza e controllo sui tuoi dati</HeaderSubtitle>
        <BackButton onClick={() => navigate('/')}>
          ← Torna alla Home
        </BackButton>
      </Header>

      <ContentContainer>
        <ContentBody>
          <UpdateDate>
            📅 <strong>Ultimo aggiornamento:</strong> 5 settembre 2024
          </UpdateDate>
          
          <SectionTitle>INTRODUZIONE</SectionTitle>
          <p>
            Questa informativa descrive come <HighlightText>Finday</HighlightText> (sito web e app mobile) 
            raccoglie, utilizza e protegge le tue informazioni personali sia attraverso il nostro sito web 
            che tramite l'applicazione mobile.
          </p>
          
          <SectionTitle>TITOLARE DEL TRATTAMENTO</SectionTitle>
          <InfoBox type="info">
            <InfoTitle type="info">📋 Informazioni di contatto</InfoTitle>
            <p>
              <strong>Nome:</strong> Giulio Polentarutti<br/>
              <strong>Email:</strong> info@finday.it<br/>
              <strong>Sito web:</strong> https://finday.it<br/>
              <strong>App:</strong> Finday - Scopri eventi nella tua zona
            </p>
          </InfoBox>
          
          <SectionTitle>DATI RACCOLTI</SectionTitle>
          
          <SubSectionTitle>1. 📍 DATI DI LOCALIZZAZIONE</SubSectionTitle>
          <StyledList>
            <li>Raccogliamo la tua posizione geografica approssimativa</li>
            <li><strong>SCOPO:</strong> Determinare e mostrare eventi nella tua zona</li>
            <li><strong>BASE LEGALE:</strong> Consenso dell'utente</li>
            <li><strong>CONSERVAZIONE:</strong> Non memorizzati permanentemente</li>
          </StyledList>
          
          <SubSectionTitle>2. 🔧 DATI TECNICI (Anonimi)</SubSectionTitle>
          <StyledList>
            <li>Informazioni tecniche per il corretto funzionamento dell'app e del sito</li>
            <li>Dati per la risoluzione di eventuali errori</li>
            <li><strong>SCOPO:</strong> Garantire il funzionamento ottimale dei nostri servizi</li>
          </StyledList>

          <SubSectionTitle>3. 🍪 COOKIE E PUBBLICITÀ (SITO WEB)</SubSectionTitle>
          <InfoBox type="warning">
            <InfoTitle type="warning">🍪 Il nostro sito web utilizza cookie e servizi di terze parti per la pubblicità</InfoTitle>
            
            <SubSectionTitle style={{ color: '#d63031', marginTop: '15px' }}>GOOGLE ADSENSE</SubSectionTitle>
            <StyledList>
              <li>Google utilizza cookie per personalizzare gli annunci mostrati sul nostro sito</li>
                            <li>Raccoglie informazioni sui tuoi interessi per mostrarti pubblicità pertinente</li>
              <li>Può tracciare la tua navigazione su più siti web</li>
              <li><strong>Gestisci le tue preferenze:</strong> <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={{color: '#3498db'}}>https://adssettings.google.com</a></li>
            </StyledList>

            <SubSectionTitle style={{ color: '#d63031', marginTop: '15px' }}>GOOGLE ANALYTICS</SubSectionTitle>
            <StyledList>
              <li>Utilizziamo Google Analytics per analizzare l'utilizzo del sito</li>
              <li>Raccoglie dati anonimi su pagine visitate, tempo di permanenza, dispositivo utilizzato</li>
              <li>Ci aiuta a migliorare l'esperienza utente</li>
              <li><strong>Informazioni:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{color: '#3498db'}}>Google Privacy Policy</a></li>
            </StyledList>

            <SubSectionTitle style={{ color: '#d63031', marginTop: '15px' }}>TIPI DI COOKIE UTILIZZATI</SubSectionTitle>
            <StyledList>
              <li><strong>Cookie tecnici:</strong> Necessari per il funzionamento del sito</li>
              <li><strong>Cookie analitici:</strong> Google Analytics per statistiche anonime</li>
              <li><strong>Cookie pubblicitari:</strong> Google AdSense per annunci personalizzati</li>
              <li><strong>Cookie di profilazione:</strong> Per personalizzare contenuti e pubblicità</li>
            </StyledList>

            <SubSectionTitle style={{ color: '#d63031', marginTop: '15px' }}>GESTIONE DEI COOKIE</SubSectionTitle>
            <p>Puoi gestire i cookie attraverso:</p>
            <StyledList>
              <li><strong>Impostazioni browser:</strong> Disabilita o elimina cookie dalle impostazioni</li>
              <li><strong>Google Ad Settings:</strong> <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={{color: '#3498db'}}>Personalizza annunci Google</a></li>
              <li><strong>Opt-out:</strong> <a href="http://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" style={{color: '#3498db'}}>Your Online Choices</a></li>
            </StyledList>
            <p><em>⚠️ Disabilitare i cookie potrebbe influire sulla funzionalità del sito.</em></p>
          </InfoBox>

          <SubSectionTitle>4. 📢 PUBBLICITÀ DI TERZE PARTI</SubSectionTitle>
          <InfoBox type="success">
            <InfoTitle type="success">📢 Partner pubblicitari che possono raccogliere dati</InfoTitle>
            <StyledList>
              <li><strong>Google AdSense:</strong> Network pubblicitario principale</li>
              <li><strong>Altri network autorizzati:</strong> Possono essere aggiunti in futuro</li>
            </StyledList>
            <p>Questi servizi utilizzano cookie e tecnologie simili per:</p>
            <StyledList>
              <li>Personalizzare gli annunci in base ai tuoi interessi</li>
              <li>Misurare l'efficacia delle campagne pubblicitarie</li>
              <li>Prevenire frodi pubblicitarie</li>
              <li>Fornire report aggregati agli inserzionisti</li>
            </StyledList>
          </InfoBox>
          
          <SectionTitle>COME UTILIZZIAMO I DATI</SectionTitle>
                    <StyledList>
            <li>Mostrare eventi pertinenti alla tua posizione geografica</li>
            <li>Calcolare la distanza tra la tua posizione e gli eventi</li>
            <li>Filtrare eventi per vicinanza geografica</li>
            <li>Migliorare l'esperienza utente dell'app e del sito web</li>
            <li>Personalizzare la pubblicità mostrata (tramite Google AdSense)</li>
            <li>Analizzare l'utilizzo del sito per miglioramenti (tramite Google Analytics)</li>
            <li>Garantire la sicurezza e prevenire abusi</li>
          </StyledList>
          
          <SectionTitle>CONDIVISIONE DEI DATI</SectionTitle>
          <InfoBox type="success">
            <InfoTitle type="success">✅ La nostra garanzia</InfoTitle>
            <p><strong>NON vendiamo, affittiamo o condividiamo i tuoi dati personali con terze parti per scopi commerciali diretti.</strong></p>
          </InfoBox>
          
          <p>I dati possono essere condivisi con:</p>
          <StyledList>
            <li><strong>Google (AdSense/Analytics):</strong> Per pubblicità e analisi come descritto sopra</li>
            <li><strong>Fornitori di servizi tecnici:</strong> Necessari per il funzionamento dell'app e del sito</li>
            <li><strong>Autorità competenti:</strong> Se richiesto dalla legge</li>
            <li><strong>Fornitori di hosting:</strong> DigitalOcean e altri servizi infrastrutturali</li>
          </StyledList>

          <SectionTitle>BASE LEGALE DEL TRATTAMENTO</SectionTitle>
          <InfoBox type="info">
            <InfoTitle type="info">⚖️ Base legale secondo GDPR</InfoTitle>
            <StyledList>
              <li><strong>Consenso:</strong> Per geolocalizzazione e cookie non tecnici</li>
              <li><strong>Interesse legittimo:</strong> Per analisi statistiche e miglioramento servizi</li>
              <li><strong>Esecuzione contratto:</strong> Per fornire il servizio richiesto</li>
              <li><strong>Obbligo legale:</strong> Per adempiere a obblighi di legge</li>
            </StyledList>
          </InfoBox>
          
          <SectionTitle>CONSERVAZIONE DEI DATI</SectionTitle>
          <StyledList>
            <li><strong>Dati di localizzazione:</strong> Non memorizzati permanentemente sul nostro server</li>
            <li><strong>Dati tecnici:</strong> Conservati per 24 mesi o tempo necessario alla risoluzione di problemi</li>
            <li><strong>Cookie:</strong> Durata variabile (da sessione a 2 anni) secondo le impostazioni Google</li>
            <li><strong>Log di accesso:</strong> Conservati per 12 mesi per motivi di sicurezza</li>
            <li>Puoi richiedere la cancellazione completa dei tuoi dati in qualsiasi momento</li>
          </StyledList>
          
          <SectionTitle>TRASFERIMENTO DATI FUORI UE</SectionTitle>
          <InfoBox type="warning">
            <InfoTitle type="warning">🌍 Trasferimenti internazionali</InfoTitle>
            <p>Alcuni nostri fornitori di servizi (Google, DigitalOcean) possono trasferire dati fuori dall'Unione Europea:</p>
            <StyledList>
              <li><strong>Google:</strong> Trasferimenti basati su Decisioni di Adeguatezza UE e Standard Contractual Clauses</li>
              <li><strong>DigitalOcean:</strong> Server principalmente in UE, trasferimenti protetti da garanzie adeguate</li>
              <li>Tutti i trasferimenti rispettano le normative GDPR e utilizzano meccanismi di protezione appropriati</li>
            </StyledList>
          </InfoBox>
          
          <SectionTitle>SICUREZZA</SectionTitle>
          <p>Utilizziamo misure tecniche e organizzative appropriate per proteggere i tuoi dati da:</p>
          <StyledList>
            <li>Accessi non autorizzati</li>
            <li>Perdite accidentali</li>
            <li>Modifiche non autorizzate</li>
            <li>Divulgazioni improprie</li>
            <li>Attacchi informatici e malware</li>
          </StyledList>
          
          <SectionTitle>I TUOI DIRITTI</SectionTitle>
          <p>In conformità al GDPR, hai diritto a:</p>
          <InfoBox type="success">
            <InfoTitle type="success">🔐 I tuoi diritti fondamentali</InfoTitle>
            <StyledList>
              <li><strong>Accesso (Art. 15):</strong> Conoscere quali dati trattiamo su di te</li>
              <li><strong>Rettifica (Art. 16):</strong> Correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione (Art. 17):</strong> Richiedere la cancellazione dei tuoi dati ("diritto all'oblio")</li>
              <li><strong>Limitazione (Art. 18):</strong> Limitare il trattamento dei tuoi dati</li>
              <li><strong>Opposizione (Art. 21):</strong> Opporti al trattamento per motivi legittimi</li>
              <li><strong>Portabilità (Art. 20):</strong> Ricevere i tuoi dati in formato strutturato</li>
              <li><strong>Revoca consenso:</strong> Revocare il consenso in qualsiasi momento</li>
              <li><strong>Reclamo:</strong> Presentare reclamo al Garante Privacy</li>
            </StyledList>
          </InfoBox>
          
          <SectionTitle>MINORI</SectionTitle>
          <InfoBox type="warning">
            <InfoTitle type="warning">👶 Protezione dei minori</InfoTitle>
            <p>I nostri servizi non sono destinati a minori di 16 anni (o età minima prevista nel tuo paese). Non raccogliamo consapevolmente dati personali di minori. Se veniamo a conoscenza di aver raccolto dati di un minore, procederemo immediatamente alla loro cancellazione e informeremo i genitori/tutori.</p>
          </InfoBox>
          
          <SectionTitle>MODIFICHE A QUESTA POLICY</SectionTitle>
          <p>Potremmo aggiornare questa informativa sulla privacy. Ti informeremo di eventuali modifiche significative tramite:</p>
          <StyledList>
            <li>Notifica nell'app mobile</li>
            <li>Banner informativo sul sito web</li>
            <li>Email all'indirizzo fornito (se disponibile)</li>
            <li>Aggiornamento della data in questa pagina</li>
          </StyledList>
          
          <SectionTitle>CONTATTI</SectionTitle>
          <ContactBox>
            <ContactTitle>📧 CONTATTI PER LA PRIVACY</ContactTitle>
            <p><strong>Per domande su questa informativa o per esercitare i tuoi diritti GDPR:</strong></p>
            <p>
              <strong>Email:</strong> info@finday.it<br/>
              <strong>Oggetto email:</strong> "Richiesta Privacy - [Il tuo diritto]"<br/>
                            <strong>Titolare del trattamento:</strong> Giulio Polentarutti<br/>
              <strong>Risposta garantita entro:</strong> 30 giorni dal ricevimento<br/>
            </p>
            
            <div style={{marginTop: '20px'}}>
              <ContactButton href="mailto:info@finday.it?subject=Richiesta Privacy">
                📧 Contatta Privacy
              </ContactButton>
              <ContactButton href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
                🎯 Gestisci Annunci
              </ContactButton>
              <ContactButton href="https://www.gpdp.it" target="_blank" rel="noopener noreferrer">
                🛡️ Garante Privacy
              </ContactButton>
            </div>
          </ContactBox>
          
          <SectionTitle>CONSENSO</SectionTitle>
          <InfoBox type="success">
            <InfoTitle type="success">✅ Utilizzando Finday acconsenti a</InfoTitle>
            <StyledList>
              <li>Il trattamento dei tuoi dati di localizzazione come descritto in questa informativa</li>
              <li>L'utilizzo di cookie tecnici necessari per il funzionamento del sito</li>
              <li>Il trattamento di dati anonimi per analisi statistiche</li>
            </StyledList>
            
            <p><strong>Per cookie pubblicitari e di profilazione:</strong></p>
            <p>Il consenso viene richiesto tramite banner cookie al primo accesso al sito. Puoi modificare le tue preferenze in qualsiasi momento.</p>
          </InfoBox>

          <SectionTitle>📋 RIASSUNTO SEMPLICE</SectionTitle>
          <SummaryGrid>
            <SummaryCard type="do">
              <SummaryTitle type="do">✅ COSA FACCIAMO</SummaryTitle>
              <StyledList style={{fontSize: '14px', margin: '10px 0 0 0'}}>
                <li>Raccogliamo la tua posizione per mostrarti eventi vicini</li>
                <li>Utilizziamo Google AdSense per gli annunci pubblicitari</li>
                <li>Analizziamo l'uso del sito per migliorarlo</li>
              </StyledList>
            </SummaryCard>
            
            <SummaryCard type="dont">
              <SummaryTitle type="dont">❌ COSA NON FACCIAMO</SummaryTitle>
              <StyledList style={{fontSize: '14px', margin: '10px 0 0 0'}}>
                <li>Non vendiamo i tuoi dati personali</li>
                <li>Non tracciamo senza consenso</li>
                <li>Non memorizziamo la posizione permanentemente</li>
              </StyledList>
            </SummaryCard>
            
            <SummaryCard type="control">
              <SummaryTitle type="control">🎯 I TUOI CONTROLLI</SummaryTitle>
              <StyledList style={{fontSize: '14px', margin: '10px 0 0 0'}}>
                <li>Disabilita geolocalizzazione quando vuoi</li>
                <li>Gestisci cookie dal browser</li>
                <li>Richiedi cancellazione dati</li>
              </StyledList>
            </SummaryCard>
          </SummaryGrid>
          
          <InfoBox type="success" style={{textAlign: 'center', marginTop: '30px'}}>
            <InfoTitle type="success" style={{fontSize: '1.2rem'}}>
              🛡️ La tua privacy è importante per noi. Hai sempre il controllo dei tuoi dati!
            </InfoTitle>
          </InfoBox>
        </ContentBody>
      </ContentContainer>

      <div style={{
        maxWidth: '900px',
        margin: '40px auto 0',
        textAlign: 'center',
        color: '#7f8c8d',
        background: 'white',
        padding: '30px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
      }}>
        <p style={{marginBottom: '10px', fontSize: '16px', fontWeight: '600'}}>
          <strong>© 2024 Finday</strong> - Sviluppato da Giulio Polentarutti
        </p>
        <p style={{marginBottom: '20px', fontSize: '14px'}}>
          Informativa Privacy conforme a GDPR (EU) 2016/679 e normative italiane
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '15px',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <ContactButton 
            href="/" 
            style={{
              background: 'linear-gradient(135deg, #FF9500, #FFB84D)',
              border: 'none'
            }}
          >
            🏠 Torna alla Home
          </ContactButton>
          <ContactButton 
            href="mailto:info@finday.it?subject=Richiesta Privacy"
            style={{
              background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
              border: 'none'
            }}
          >
            📧 Contatta Privacy
          </ContactButton>
          <ContactButton 
            href="https://adssettings.google.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              background: 'linear-gradient(135deg, #f39c12, #e67e22)',
              border: 'none'
            }}
          >
            🎯 Gestisci Annunci
          </ContactButton>
        </div>
        
        <div style={{
          fontSize: '12px',
          color: '#95a5a6',
          borderTop: '1px solid #ecf0f1',
          paddingTop: '20px'
        }}>
          <p>
            Questa informativa è stata aggiornata per essere conforme alle politiche di Google AdSense 
            e garantire la massima trasparenza sui dati raccolti. Ultimo aggiornamento: 5 settembre 2024.
          </p>
        </div>
      </div>
    </PrivacyContainer>
  );
}; 

export default Privacy;
